
/**
 * MapUIContext - Combined context for backward compatibility
 * 
 * This context combines LocationModalContext and MapVisibilityContext for
 * backward compatibility with existing code that uses useMapUI().
 * 
 * For new code or performance-sensitive components:
 * - Use useLocationModal() for modal state only (prevents re-renders from map visibility changes)
 * - Use useMapVisibility() for map visibility only (prevents re-renders from modal state changes)
 * 
 * The split contexts prevent cascade re-renders when only one concern changes.
 */

import React, { createContext, useContext } from 'react';
import { LocationModalProvider, useLocationModal } from './LocationModalContext';
import { MapVisibilityProvider, useMapVisibilityContext, useMapVisibility } from './MapVisibilityContext';

// SelectedLocation accepts various location shapes from different sources
// (PostLocationExtended, Location, raw location data). A proper union type
// would require significant refactoring across multiple components.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SelectedLocation = any | null;

type MapUIContextType = {
    // Modal state (from LocationModalContext)
    isOpen: boolean;
    currentRoute: string | null;
    setIsOpen: (value: boolean, route?: string | null) => void;
    selectedLocation: SelectedLocation;
    setSelectedLocation: (loc: SelectedLocation) => void;
    // Map visibility state (from MapVisibilityContext)
    initializeMapVisibility: (scope: string, defaultValue: boolean) => void;
    getMapVisibility: (scope: string, defaultValue?: boolean) => boolean;
    setMapVisibility: (scope: string, value: boolean) => void;
    toggleMapVisibility: (scope: string, defaultValue?: boolean) => void;
};

// This context is kept for backward compatibility but now combines both split contexts
const MapUIContext = createContext<MapUIContextType | undefined>(undefined);

/**
 * Combined provider that wraps both LocationModalProvider and MapVisibilityProvider.
 * Use this for backward compatibility with existing code.
 */
export const MapUIProvider = ({ children }: { children: React.ReactNode }) => {
    return (
        <LocationModalProvider>
            <MapVisibilityProvider>
                <MapUICombiner>
                    {children}
                </MapUICombiner>
            </MapVisibilityProvider>
        </LocationModalProvider>
    );
};

/**
 * Internal component that combines the two contexts into one for backward compatibility.
 */
const MapUICombiner = ({ children }: { children: React.ReactNode }) => {
    const modalContext = useLocationModal();
    const visibilityContext = useMapVisibilityContext();

    const combinedValue: MapUIContextType = {
        // Modal state
        isOpen: modalContext.isOpen,
        currentRoute: modalContext.currentRoute,
        setIsOpen: modalContext.setIsOpen,
        selectedLocation: modalContext.selectedLocation,
        setSelectedLocation: modalContext.setSelectedLocation,
        // Map visibility state
        initializeMapVisibility: visibilityContext.initializeMapVisibility,
        getMapVisibility: visibilityContext.getMapVisibility,
        setMapVisibility: visibilityContext.setMapVisibility,
        toggleMapVisibility: visibilityContext.toggleMapVisibility,
    };

    return (
        <MapUIContext.Provider value={combinedValue}>
            {children}
        </MapUIContext.Provider>
    );
};

/**
 * Combined hook for backward compatibility.
 * 
 * WARNING: Using this hook will cause re-renders when EITHER modal state OR
 * map visibility state changes. For better performance, use:
 * - useLocationModal() for modal-only operations
 * - useMapVisibility() for map visibility-only operations
 */
export const useMapUI = () => {
    const context = useContext(MapUIContext);
    if (!context) throw new Error('useMapUI must be used within a MapUIProvider');
    return context;
};

// Re-export useMapVisibility for backward compatibility
export { useMapVisibility };
